<template>
  <v-container v-if="credit.id" fluid class="pa-6 product-detail">
    <v-row dense class="main-equalizer equalize-columns">
      <v-col cols="12" md="9" class="left-column">
        <!-- =============================
          SALDO VENCIDO Y BURBUJA
        ============================== -->
        <v-card class="section-card header-info" elevation="3">
          <v-card-text>
            <div class="text-h4 font-weight-bold mb-6">
              {{ credit.product?.name }} - {{ credit.folio }}
            </div>

            <v-row dense class="align-center">
              <!-- IZQUIERDA -->
              <v-col cols="12" md="7">
                <div v-if="credit.id" class="d-flex flex-column">
                  <template v-if="dueAmount > 0">
                    <!-- CON SALDO VENCIDO -->
                    <div class="text-subtitle-1 font-weight-bold">
                      SALDO VENCIDO
                    </div>

                    <div class="text-h4 font-weight-bold">
                      {{ formatCurrency(dueAmount) }} {{ credit.moneda }}
                    </div>

                    <div class="d-flex flex-wrap mt-3">
                      <div class="mini-detail">
                        <span class="mini-label">Capital</span>
                        <span class="mini-value">{{ formatCurrency(credit.currentBalance?.balance?.capitalVencido) }}</span>
                      </div>

                      <div class="mini-detail">
                        <span class="mini-label">Intereses</span>
                        <span class="mini-value">{{ formatCurrency(credit.currentBalance?.balance?.interest) }}</span>
                      </div>

                      <div class="mini-detail">
                        <span class="mini-label">Moratorios</span>
                        <span class="mini-value">{{ formatCurrency(credit.currentBalance?.balance?.moratorium) }}</span>
                      </div>
                    </div>
                  </template>

                  <!-- SIN SALDO VENCIDO -->
                  <template v-else>
                    <div class="d-flex align-center mt-2">
                      <v-icon
                        color="success"
                        class="mr-2"
                        medium
                      >
                        mdi-check-circle
                      </v-icon>

                      <div class="text-h5 font-weight-medium success--text">
                        Al corriente
                      </div>
                    </div>

                    <div class="grey--text text--darken-1 mt-1">
                      No se registran pagos vencidos.
                    </div>
                  </template>
                </div>
              </v-col>

              <!-- DERECHA -->
              <v-col v-if="credit.id" cols="12" md="5" class="d-flex align-center justify-end">
                <v-card
                  flat
                  class="next-cut-card compact pa-4 text-center"
                  :class="{
                    'warning-state': totalDueAndUpcoming > 0 && currentPeriod.daysTillEnd <= 7 && currentPeriod.daysTillEnd > 0,
                    'danger-state': totalDueAndUpcoming > 0 && currentPeriod.daysTillEnd === 0
                  }"
                >
                  <v-icon size="32" class="mb-1 icon-colored">mdi-calendar</v-icon>

                  <div v-if="currentPeriod.daysTillEnd <= 1">
                    <div class="days-remaining small">
                      {{ currentPeriod.daysTillEnd === 1 ? 'Mañana' : 'Hoy' }}
                    </div>
                    <div class="text-subtitle-1 font-weight-bold">
                      Es fecha de corte
                    </div>
                  </div>

                  <div v-else>
                    <div class="days-remaining small">
                      {{ currentPeriod.daysTillEnd }} días
                    </div>

                    <div class="text-subtitle-1 font-weight-bold">
                      Para el próximo corte
                    </div>
                  </div>

                  <div v-if="dueAmount > 0 && currentPeriod.daysTillEnd <= 7">
                    <div class="table-label">
                      Monto para regularizar:
                    </div>

                    <div class="small font-weight-bold">
                      {{ formatCurrency(totalDueAndUpcoming) }} {{ credit.moneda }}
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-row dense class="flex-grow-1 bottom-sections mt-2">
          <!-- =============================
            ÚLTIMO PAGO
          ============================== -->
          <v-col cols="12" md="4" class="d-flex section-wrapper">
            <v-card class="section-card last-payment pa-4" elevation="3">
              <v-card-text>
                <div class="text-h6 font-weight-bold mb-4">
                  Último pago realizado
                </div>

                <div v-if="paymentsHistory.length">
                  <div class="payment-row">
                    <span>Monto:</span>
                    <span class="payment-value">
                      {{ formatCurrency(-lastPayment.payment?.pago) }}
                      {{ credit.moneda }}
                    </span>
                  </div>

                  <div class="payment-row">
                    <span>Fecha:</span>
                    <span class="payment-value">
                      {{ formatDateShortText(lastPayment.payDate) }}
                    </span>
                  </div>

                  <v-divider class="my-4" />

                  <!-- Descargar complemento -->
                  <v-menu offset-y transition="slide-y-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="text-center mb-3">
                        <v-btn
                          color="primary"
                          outlined
                          small
                          v-bind="attrs"
                          v-on="on"
                          :disabled="!lastPayment?.invoice?.facturamaId"
                        >
                          <v-icon left small>mdi-download</v-icon>
                          <span>
                            {{ $vuetify.breakpoint.mdOnly
                              ? 'Descargar CP'
                              : 'Descargar Complemento' }}
                          </span>
                        </v-btn>
                      </div>
                    </template>

                    <v-list dense class="py-0">
                      <v-list-item @click="onDownloadPaymentInvoice(lastPayment, 'pdf')">
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-file-pdf-box</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>PDF</v-list-item-title>
                      </v-list-item>

                      <v-list-item @click="onDownloadPaymentInvoice(lastPayment, 'xml')">
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-xml</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>XML</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <!-- Historial completo -->
                  <div class="text-center">
                    <v-btn
                      color="primary"
                      outlined
                      small
                      :disabled="false"
                      @click="openHistoryPayments()"
                    >
                      <v-icon left small>mdi-history</v-icon>
                      <span>{{ $vuetify.breakpoint.mdOnly ? 'Ver historial' : 'Ver historial de pagos' }}</span>
                    </v-btn>
                  </div>

                  <payments-history
                    v-model="showPaymentsHistory"
                    :payments="paymentsHistory"
                    :currency="credit.moneda"
                    @download-invoice="onDownloadPaymentInvoice"
                  />
                </div>

                <div class="payment-row" v-else-if="credit.id">
                  <span>
                    El crédito no presenta pagos registrados hasta el momento.
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Siguiente corte -->
          <v-col cols="12" md="4" class="d-flex section-wrapper">
            <v-card class="section-card next-payment pa-4" elevation="3">
              <v-card-text>

                <div class="text-h6 font-weight-bold mb-4">Próximo corte</div>

                <div class="payment-row">
                  <span>Monto:</span>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        class="payment-value"
                        v-bind="attrs"
                        v-on="on"
                        style="cursor: help;"
                      >
                        {{ formatCurrency(currentPeriod?.montoPago - currentPeriod?.montoPagado) }} {{ credit.moneda }}
                          <v-icon small class="ml-1" color="#46659B">
                            mdi-information-outline
                          </v-icon>
                      </span>
                    </template>

                    <span>
                      {{ interestFixed ? 'Monto exacto' : 'Monto estimado, sujeto a variación por tasa variable' }}
                    </span>
                  </v-tooltip>
                </div>

                <div class="payment-row">
                  <span>Fecha:</span>
                  <span class="payment-value">{{ formatDateShortText(currentPeriod?.fechaFin) }}</span>
                </div>

                <v-divider class="my-4" />

                <v-row dense class="text-center">
                  <v-col cols="6">
                    <div class="table-label">Capital</div>
                    <div class="table-value">{{ formatCurrency(currentPeriod?.pagoCapital) }}</div>
                  </v-col>

                  <v-col cols="6">
                    <div class="table-label">Intereses</div>
                    <div class="table-value">{{ formatCurrency(currentPeriod?.pagoInteres) }}</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- =============================
            EDO DE CUENTA
          ============================== -->
          <v-col cols="12" md="4" class="d-flex section-wrapper">
            <v-card class="section-card account-statement pa-4" elevation="3">
              <v-card-text>
                <div class="text-h6 font-weight-bold mb-4">Estado de cuenta</div>

                <v-row dense>
                  <v-col cols="12">
                    <v-select
                      v-model="selectedPeriod"
                      :items="periodOptions"
                      label="Periodo"
                      dense
                      outlined
                      hide-details
                    />
                  </v-col>
                </v-row>

                <v-divider class="my-4" />

                <div class="text-center">
                  <v-btn
                    color="primary"
                    outlined
                    small
                    :disabled="!selectedPeriod"
                    @click="onDownloadAccountStatement"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    <span>{{ $vuetify.breakpoint.mdOnly ? 'Descargar' : 'Descargar edo. de cuenta' }}</span>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="3" class="right-column">
        <!-- =============================
            TIPO DE CAMBIO
        ============================== -->
        <v-card class="section-card pa-4" elevation="3">
          <div class="d-flex align-center justify-space-between">
            <div class="text-subtitle-1 font-weight-medium grey--text text--darken-1">
              Tipo de cambio
            </div>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="d-flex align-center"
                  v-bind="attrs"
                  v-on="on"
                  style="cursor: help;"
                >
                  <span class="text-subtitle-1 font-weight-bold mr-1">
                    {{ indicators?.USD }}
                  </span>

                  <span class="text-caption grey--text text--darken-1">
                    MXN / USD
                  </span>

                  <v-icon small class="ml-1" color="#46659B">
                    mdi-information-outline
                  </v-icon>
                </div>
              </template>

              <div>
                <strong>Fuente:</strong> {{ indicators?.SOURCE }} <br>
                <strong>Fecha:</strong> {{ formatDateShortText(indicators?.EXCHANGEDATE) }}
              </div>
            </v-tooltip>
          </div>
        </v-card>

        <!-- =============================
          AVANCE DE CAPITAL
        ============================== -->
        <v-card class="section-card pa-4 mt-2" elevation="3">
          <div class="text-h6 font-weight-bold mb-4 text-center">Avance del capital</div>

          <apexchart
            width="100%"
            type="radialBar"
            :options="chartOptions"
            :series="[capitalPercent]"
          />

          <div class="text-center mt-4">
            <div class="text-subtitle-2">Capital pendiente</div>
            <div class="text-h5 font-weight-bold mt-1">
              {{ formatCurrency(credit.currentBalance?.balance?.capital) }} {{ credit.moneda }}
            </div>
          </div>

          <v-divider class="my-4" />

          <div class="text-center mb-4">
            <div class="text-subtitle-2">Monto para liquidar</div>
            <div class="text-h6 font-weight-bold mt-1">
              {{ formatCurrency(credit.currentBalance?.balance?.toLiquidate) }} {{ credit.moneda }}
            </div>
          </div>

          <v-divider class="my-4" />

          <div class="text-center">
            <div class="text-subtitle-2">Periodos restantes</div>
            <div class="text-h6 font-weight-bold mt-1">{{ periodsLeft }}</div>
          </div>
        </v-card>
      </v-col>

    </v-row>

    <!-- =============================
         DETALLE DEL CRÉDITO
    ============================== -->
    <v-row dense class="mt-2">
      <v-col cols="12">
        <v-card class="section-card credit-details pa-4" elevation="3">
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="text-h6 font-weight-bold">Detalle del crédito</div>

              <v-btn
                small
                outlined
                color="primary"
                @click="showAmortization = true"
              >
                <v-icon left small>mdi-calendar-check</v-icon>
                Ver tabla de amortización
              </v-btn>
            </div>

            <amortization-schedule
              v-model="showAmortization"
              :data="originalAmortizationSchedule"
              :currency="credit.moneda"
              :folio="credit.folio"
            />

            <v-row dense>
              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Monto</div>
                <div class="credit-value">
                  {{ formatCurrency(credit.montoInicial) }} {{ credit.moneda }}
                </div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Fecha de inicio</div>
                <div class="credit-value">{{ formatDateShortText(credit.fechaInicioCredito) }}</div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Fin de fin</div>
                <div class="credit-value">{{ formatDateShortText(credit.fechaFinCredito) }}</div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Días del año</div>
                <div class="credit-value">{{ credit.diasAnio }}</div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Tasa ordinaria</div>
                <!-- TASA VARIABLE -->
                <template v-if="!interestFixed">
                  <div class="credit-value d-flex align-center">

                    <!-- Tooltip SOLO para el valor -->
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                          style="cursor: help;"
                        >
                          {{ interestRate }}
                        </span>
                      </template>

                      <div>
                        <strong>Tasa variable:</strong> {{ interestRateValue }} <br>
                        <strong>Fuente:</strong> {{ indicators.SOURCE }} <br>
                        <strong>Fecha:</strong>
                        {{ formatDateShortText(indicators.EXCHANGEDATE) }}
                      </div>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          outlined
                          class="ml-1"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="openRateHistory()"
                          aria-label="Ver historial de tasas"
                        >
                          <v-icon small>mdi-chart-timeline-variant</v-icon>
                        </v-btn>
                      </template>

                      <span>Ver historial de tasas</span>
                    </v-tooltip>

                  </div>
                </template>

                <!-- TASA FIJA -->
                <template v-else>
                  <div class="credit-value">
                    {{ interestRate }}
                  </div>
                </template>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <div class="credit-label">Tasa moratoria</div>
                <div
                  class="credit-value"
                  :class="{ 'moratoria-activa': credit.currentBalance?.daysOverDue }"
                >
                  {{ interestFixed ? 'FIJA ' +  formatRate(credit.moraAnual || 0) + '%' : `${credit.product?.confs?.moratoriumMultiplier}x la tasa ordinaria`}}
                  <span v-if="credit.currentBalance?.daysOverDue" class="moratoria-tag">Activa</span>

                  <!-- Botón para ver historial de tasas moratorias, solo si el crédito es tasa variable -->
                  <v-tooltip v-if="!interestFixed" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        outlined
                        class="ml-1"
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="openRateHistory(true)"
                        aria-label="Ver historial de tasas"
                      >
                        <v-icon small>mdi-chart-timeline-variant</v-icon>
                      </v-btn>
                    </template>

                    <span>Ver historial de tasas</span>
                  </v-tooltip>
                </div>
              </v-col>

              <rate-history
                v-model="showRateHistory"
                :data="rateHistory"
                :interestAdditional="showLatePaymentRateHistory ? 0 : parseFloat(credit.interesBaseAdicional || 0)"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import CreditsManagerService from '../../services/CreditsManagerService'
import productMixin from '../../mixins/productMixin'
import PaymentsHistory from '../../components/Modal/PaymentsHistory.vue'
import AmortizationSchedule from '../../components/Modal/AmortizationSchedule.vue'
import RateHistory from '../../components/Modal/RateHistory.vue'

export default {
  name: 'AmortizableDetail',
  mixins: [productMixin],
  components: { PaymentsHistory, AmortizationSchedule, RateHistory },
  data () {
    return {
      credit: {},
      creditId: null,
      currentPeriod: {},
      selectedPeriod: null,
      paymentsHistory: [],
      showPaymentsHistory: false,
      showAmortization: false,
      showRateHistory: false,
      showLatePaymentRateHistory: false,
      rateHistory: [],
      originalAmortizationSchedule: []
    }
  },
  watch: {
    activePortafolio (portafolio) {
      if (!this.$route.query.folio) return

      const credit = portafolio?.find(c => c.folio === this.$route.query.folio)
      if (!credit) {
        this.$swal({
          icon: 'error',
          title: 'Folio inexistente',
          text: 'Este producto no se encuentra dentro de tu portafolio.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#d93025'
        })
        this.redirectToHome()
      }

      this.creditId = credit.id
      this.loadCredit()
    },
    '$route.query.folio': {
      immediate: true,
      handler (newFolio) {
        if (newFolio) {
          this.creditId = this.activePortafolio?.find(c => c.folio === newFolio)?.id
          this.loadCredit()
        }
      }
    }
  },
  async beforeMount () {
    const isLoggedIn = this.$store?.getters?.isLoggedIn

    if (!isLoggedIn) {
      window.location.replace('/login')
    }
  },
  mounted () {
    if (!this.$route.query.folio) this.redirectToHome()
  },
  methods: {
    async loadCredit () {
      if (!this.creditId) return
      this.resetData()
      const loader = this.$loading.show()
      try {
        const response = await CreditsManagerService.getCredit(this.creditId)
        this.credit = response.data

        // Obtener tabla de amortización inicial/original
        const { data: originalAmortizationSchedule } = await CreditsManagerService.getAmortizationSchedule({
          creditId: this.creditId,
          getOriginal: true
        })
        // Periodos ya pagados sobre tabla original
        originalAmortizationSchedule.forEach(o => {
          o.paid = this.credit.tablaAmortizacion.some(t => t.montoPagado >= t.montoPago && (t.periodo) === (o.periodo))
        })
        this.originalAmortizationSchedule = originalAmortizationSchedule

        // Periodo en curso
        const today = moment().tz('America/Mexico_City')
        this.currentPeriod = response.tablaAmortizacion?.find(p => today >= new Date(p.fechaInicio) && today <= new Date(p.fechaFin)) || {}

        const endDate = moment.tz(this.currentPeriod.fechaFin, 'YYYY-MM-DD', 'America/Mexico_City')
        this.currentPeriod.daysTillEnd = endDate.diff(today, 'days') + 1

        // Historial de pagos
        const pastPayments = response.creditMovements?.filter(m => !m.payment?.tratamiento) || []

        this.paymentsHistory = pastPayments.map(p => {
          const interestFixed = this.interestFixed
          let paymentDateRate

          if (interestFixed) {
            paymentDateRate = this.credit?.tasaAnual
          } else {
            const appliedPaymentRate = response.tasasAplicables.find(t =>
              t.fecha === p.appliedPayDate || // Que tengan la misma fecha
            t.fecha.substring(0, 8) === p.appliedPayDate.substring(0, 8)) // O que pertenezcan al mismo mes

            paymentDateRate = parseFloat(appliedPaymentRate?.tasa || 0) + parseFloat(this.credit?.interesBaseAdicional || 0)
          }

          // Pago anticipado: se resta el monto ideal a lo que pagó el cliente para sacar el monto que se va a últimos pagos
          const advancePayment = -(p.payment?.pago ?? 0) - p.amount
          return {
            ...p,
            interestFixed,
            paymentDateRate,
            advancePayment
          }
        })
      } catch (err) {
        console.error(err)
        this.$swal({
          icon: 'error',
          title: 'Ocurrió un error',
          text: 'Hubo un problema al cargar los detalles de tu crédito. Por favor inténtalo nuevamente.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#d93025'
        })
        this.redirectToHome()
      } finally {
        loader.hide()
      }

      if (this.paymentsHistory?.length) {
        this.paymentsHistory = await this.mapInvoicesToPayments(this.paymentsHistory, this.credit?.fechaInicioCredito)
      }
    },
    async onDownloadAccountStatement () {
      this.downloadAccountStatement({
        id: this.credit.id,
        folio: this.credit.folio,
        selectedPeriod: this.selectedPeriod
      })
    },
    onDownloadPaymentInvoice (item, format) {
      this.downloadInvoice({
        id: item.invoice?.facturamaId,
        fileType: format,
        fileName: `Factura_CP_${this.credit.folio}_${item.payDate}`
      })
    },
    resetData () {
      this.credit = {}
      this.paymentsHistory = []
      this.currentPeriod = {}
      this.selectedPeriod = null
    },
    openHistoryPayments () {
      this.showPaymentsHistory = true
    },
    openRateHistory (isLatePaymentRate = false) {
      this.rateHistory = [
        ...new Map(
          (isLatePaymentRate ? this.credit.tasasAplicablesMoratorio : this.credit.tasasAplicables)
            .map(item => [
              `${item.tasa}-${item.fecha}`,
              item
            ]))
          .values()
      ]

      this.showLatePaymentRateHistory = isLatePaymentRate
      this.showRateHistory = true
    }
  },
  computed: {
    interestFixed () {
      return this.credit?.interesBase === 'FIXED' || false
    },
    lastPayment () {
      return this.paymentsHistory[this.paymentsHistory.length - 1] || {}
    },
    totalCapital () {
      const paidCapital = this.credit.paidBalance?.capital
      const pendingCapital = this.credit.currentBalance?.balance?.capital
      const total = paidCapital + pendingCapital
      if (isNaN(total)) return 0
      return total
    },
    capitalPercent () {
      if (!this.totalCapital) return 0

      const percentage = Math.round((this.credit.paidBalance?.capital / this.totalCapital) * 100)

      return percentage
    },
    dueAmount () {
      return this.credit.currentBalance?.balance?.totalExigible
    },
    totalDueAndUpcoming () {
      return this.dueAmount + this.currentPeriod?.montoPago - this.currentPeriod?.montoPagado
    },
    periodsLeft () {
      return this.credit?.tablaAmortizacion?.filter(t => t.montoPagado < t.montoPago).length || null
    },
    interestRate () {
      if (!this.credit.interesBase) return ''
      const base = this.interestFixed ? 'FIJA' : this.credit.interesBase
      const percent = parseFloat(this.interestFixed ? this.credit.tasaAnual : this.credit.interesBaseAdicional)
      return `${base} ${this.interestFixed ? '' : '+'} ${this.formatRate(percent)}%`
    },
    interestRateValue () {
      if (!this.credit.interesBase) return
      if (this.interestFixed) return this.credit.tasaAnual

      const baseValue = this.credit.interesBase?.toUpperCase().includes('SOFRAI') ? this.indicators.SOFRAI[this.credit.interesBase?.toUpperCase()] : this.indicators[this.credit.interesBase?.toUpperCase()]
      const percent = parseFloat(baseValue) + (parseFloat(this.credit.interesBaseAdicional) * 100)
      return `${percent.toFixed(2)}%`
    },
    chartOptions () {
      return {
        chart: {
          sparkline: { enabled: true }
        },
        plotOptions: {
          radialBar: {
            hollow: {
              size: '60%'
            },
            track: {
              background: '#BBDEF0',
              strokeWidth: '70%'
            },
            dataLabels: {
              show: true,
              value: {
                fontSize: '36px',
                fontWeight: 'bold',
                offsetY: 5
              }
            }
          }
        },
        tooltip: {
          enabled: true,
          custom: () => {
            return `
            <div class="apex-tooltip">
              <div><strong>Capital pagado:</strong> ${this.formatCurrency(this.credit.paidBalance?.capital)} ${this.credit.moneda}</div>
            </div>
          `
          }
        },
        fill: {
          colors: ['#142660']
        },
        labels: ['']
      }
    },
    periodOptions () {
      return this.getPeriodOptions(this.credit)
    }
  }
}
</script>

<style scoped>

.product-detail {
  min-height: 100vh;
}

.equalize-columns {
  align-items: stretch !important;
}

.main-equalizer {
  display: flex;
  align-items: stretch !important;
}

.left-column,
.right-column {
  display: flex;
  flex-direction: column;
}

.bottom-sections {
  flex-grow: 1;
  display: flex;
  align-items: stretch !important;
}

.section-wrapper {
  display: flex;
  flex-direction: column;
}

.section-wrapper .section-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.text-h4,
.text-h5,
.text-h6,
.product-title,
.credit-label,
.table-label,
.payment-row span:first-child,
.section-card .text-h6,
.text-subtitle-2 {
  color: #1E293B !important;
}

.next-cut-card {
  border-radius: 16px;
  background: linear-gradient(135deg, #BBDEF0, #ffffff);
  border: 1px solid #d1e3f7;
  max-width: 220px;
}

.days-remaining.small {
  font-size: 30px;
  font-weight: 800;
}

.mini-detail {
  margin-right: 24px;
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
}

.mini-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.mini-value {
  font-size: 16px;
  font-weight: 700;
  color: #1a237e;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.payment-value,
.table-value,
.credit-value {
  font-weight: 700;
  color: #1a237e;
}

.credit-label {
  font-size: 13px;
  font-weight: 600;
}

.credit-value {
  font-size: 17px;
  font-weight: 700;
}

.moratoria-activa {
  color: #c62828;
  font-weight: bold;
  position: relative;
}

.moratoria-tag {
  background-color: #c62828;
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  text-transform: uppercase;
}

.next-cut-card {
  transition: background-color 0.3s ease, color 0.3s ease;
}

.next-cut-card .icon-colored {
  color: #253670
}
.next-cut-card .days-remaining {
  color: #253670;
}

.warning-state {
  background: linear-gradient(135deg, #fff8e1, #ffffff);
  border: 1px solid #f8c88a;
}
.warning-state .icon-colored {
  color: #FF9D20;
}
.warning-state .days-remaining {
  color: #FF9D20;
}

.danger-state {
 background: linear-gradient(135deg, #ffebee, #ffffff);
  border: 1px solid rgb(247, 126, 126);
}
.danger-state .icon-colored {
  color: #c62828;
}
.danger-state .days-remaining {
  color: #c62828;
  font-weight: bold;
}

</style>
